import { useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  InlineLoading,
  ActionableNotification,
  InlineNotification,
} from '@carbon/react';
import { useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { Helmet } from 'react-helmet-async';
import ErrorToast from 'components/error-toast';
import { PageHeader } from 'components/page-header';
import { ContentHeader } from 'components/scenario-form-layout';
import { toastAtom } from 'components/scenario-area-configs/atoms';
import { SuccessToast } from 'components/success-toast';
import {
  SCENARIO_DOMAINS,
  type ScenarioDomainKey,
} from 'lib/evaluation-case/scenario-domains';
import routes from '~/routes';
import {
  CASE_QUERY_KEY,
  RUN_SCENARIOS_QUERY_KEY,
} from 'lib/evaluation-case/query-keys';
import { pageTitle } from 'utils/page-title';
import { useCase } from './api';
import { CaseDetailsFields } from './case-details-fields';
import { CloneButton } from './clone-button';
import { DomainPickerDrawer } from './domain-picker-drawer';
import { DomainTiles } from './domain-tiles';
import { CaseCoverageMap } from './coverage-map/case-coverage-map';
import { useCaseClone, useCaseForm, useCaseSave } from './hooks';
import { ReviewBanner } from './review-banner';
import {
  DEFAULT_VALUES,
  archivedScenarioIds,
  buildPayload,
  mapResponseToForm,
  validateCase,
} from './utils';
import type { CaseFormValues, CaseResponse } from './types';
import styles from './case-editor.module.scss';

interface CaseFormProps {
  caseId: number | null;
  initialValues: CaseFormValues;
  loaded: CaseResponse | null;
  /** The saved case, after a successful save. */
  onSaved: (saved: CaseResponse) => void;
  /** Reloads the case (after an acknowledgement). */
  onReload: () => void;
  /** Reloads the case and re-mounts the form from it, dropping unsaved edits (after a 409). */
  onReloadLatest: () => void;
}

/**
 * The case form: details (name and evaluation type required, nothing else), the eight domain
 * tiles, the coverage map of the picks (saved or not), the review banner, and Save Case with the
 * unsaved-changes notice. No areas or geometry priority: a case-level case has none.
 *
 * @param {CaseFormProps} props - The case, its values and save / reload handlers.
 * @return {JSX.Element} The form.
 */
function CaseForm({
  caseId,
  initialValues,
  loaded,
  onSaved,
  onReload,
  onReloadLatest,
}: CaseFormProps) {
  const history = useHistory();
  const [toast, setToast] = useAtom(toastAtom);
  const [attempted, setAttempted] = useState(false);
  const [pickerDomain, setPickerDomain] = useState<ScenarioDomainKey | null>(
    null
  );

  const form = useCaseForm(initialValues);
  // Each value on its own: the store's `values` object can be rebuilt on any notification, and a
  // subscription to it re-renders on each, which the Carbon Dropdown turns into an update loop.
  const values = {
    name: useStore(form.store, (state) => state.values.name),
    evaluationType: useStore(
      form.store,
      (state) => state.values.evaluationType
    ),
    discountToDateType: useStore(
      form.store,
      (state) => state.values.discountToDateType
    ),
    discountToDate: useStore(
      form.store,
      (state) => state.values.discountToDate
    ),
  };
  const scenarios = useStore(form.store, (state) => state.values.scenarios);
  // Whether a field differs from the saved case: the one rule for Clone, the "Unsaved changes"
  // notice and Save. An edit undone leaves TanStack's isDirty set, but nothing unsaved, and Save
  // would send an empty diff.
  const unsaved = useStore(
    form.store,
    (state) =>
      Object.keys(buildPayload(state.values, initialValues).evaluation_case)
        .length > 0
  );
  const { cloning, clone } = useCaseClone(caseId, (message) =>
    setToast({ message, type: 'error' })
  );
  const { saving, stale, save } = useCaseSave({
    caseId,
    initialValues,
    loaded,
    isTouched: useStore(form.store, (state) => state.isTouched),
    getValues: () => form.state.values,
    onSaved: (saved) => {
      setToast({ message: 'Case saved', type: 'success' });
      onSaved(saved);
    },
    onError: (message) => setToast({ message, type: 'error' }),
  });
  const errors = validateCase({ ...form.state.values, ...values });
  const invalid = Boolean(
    errors.name || errors.evaluationType || errors.discountToDate
  );

  const handleSave = async () => {
    setAttempted(true);
    if (!invalid) await save();
  };

  const archivedIds = useMemo(() => archivedScenarioIds(loaded), [loaded]);
  const suggestions = useMemo(
    () =>
      Object.fromEntries(
        (loaded?.migrationSuggestions ?? []).map((suggestion) => [
          suggestion.domain,
          suggestion,
        ])
      ),
    [loaded]
  );
  const pickerConfig = SCENARIO_DOMAINS.find(
    (domain) => domain.key === pickerDomain
  );
  const setPick = (
    domain: ScenarioDomainKey,
    choice: CaseFormValues['scenarios'][ScenarioDomainKey]
  ) => form.setFieldValue('scenarios', { ...scenarios, [domain]: choice });
  const closeToast = () => {
    setToast(null);
    return true;
  };

  return (
    <div className={styles.page}>
      {toast?.type === 'error' && (
        <ErrorToast message={toast.message} onClose={closeToast} />
      )}
      {toast?.type === 'success' && (
        <SuccessToast message={toast.message} onClose={closeToast} />
      )}

      <PageHeader
        title={loaded?.name || 'New Evaluation Case'}
        breadcrumbs={[]}
        actions={
          <div className={styles.headerActions}>
            {caseId !== null && (
              <CloneButton
                cloning={cloning}
                isDirty={unsaved}
                onClick={clone}
              />
            )}
            <Button
              kind="secondary"
              size="md"
              onClick={() => history.push(routes.evaluationCasesPath)}
            >
              Cancel
            </Button>
          </div>
        }
      />

      <div className={styles.content}>
        {loaded && caseId !== null && (
          <ReviewBanner
            caseId={caseId}
            status={loaded.reviewStatus}
            onAcknowledged={onReload}
            onError={(message) => setToast({ message, type: 'error' })}
          />
        )}

        {/* The coverage map leads the page, full width: it is what the picks below produce. The
            review banner stays above it, and Save Case with its unsaved-changes notice sits in
            the Case Details header right under it. */}
        <section className={styles.coverage} data-testid="case-coverage">
          <h5 className={styles.sectionTitle}>Coverage</h5>
          <CaseCoverageMap
            scenarios={scenarios}
            archivedScenarioIds={archivedIds}
            coverageBbox={loaded?.coverageBbox ?? null}
          />
        </section>

        {stale && (
          <ActionableNotification
            inline
            kind="warning"
            role="alert"
            hideCloseButton
            title="This case was changed by someone else since you opened it."
            subtitle="Reload to see their changes."
            actionButtonLabel="Reload"
            onActionButtonClick={onReloadLatest}
            data-testid="case-stale"
          />
        )}

        <ContentHeader
          title="Case Details"
          isDirty={unsaved}
          canSave={caseId === null || unsaved}
          isSaving={saving}
          onSave={handleSave}
          saveLabel="Save Case"
        />

        <CaseDetailsFields form={form} attempted={attempted} errors={errors} />

        <section className={styles.assignments}>
          <h5 className={styles.sectionTitle}>Scenario assignments</h5>
          <DomainTiles
            scenarios={scenarios}
            archivedScenarioIds={archivedIds}
            onEdit={setPickerDomain}
            suggestions={suggestions}
            onPick={setPick}
          />
        </section>
      </div>

      {pickerConfig && (
        <DomainPickerDrawer
          domain={pickerConfig}
          selected={scenarios[pickerConfig.key]}
          suggestion={suggestions[pickerConfig.key]}
          onSave={(choice) => {
            setPick(pickerConfig.key, choice);
            setPickerDomain(null);
          }}
          onClose={() => setPickerDomain(null)}
        />
      )}
    </div>
  );
}

/**
 * The case editor, for a new case (no id) or an existing one.
 * A save keeps the page: it re-reads the saved case and re-mounts the form clean, so the "Case
 * saved" toast stays up and the unsaved-changes notice clears.
 *
 * @return {JSX.Element} The page.
 */
export function CaseEditorPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const caseId = id ? Number(id) : null;
  const history = useHistory();
  const queryClient = useQueryClient();
  const setToast = useSetAtom(toastAtom);
  // Bumped by each save, so the form re-mounts from the saved values; a reload after an
  // acknowledgement leaves it, keeping any unsaved edits.
  const [savedVersion, setSavedVersion] = useState(0);

  const { data, isPending, isError, refetch } = useCase(caseId);

  if (caseId === null) {
    return (
      <>
        <Helmet>
          <title>{pageTitle('New Evaluation Case')}</title>
        </Helmet>
        <CaseForm
          caseId={null}
          initialValues={DEFAULT_VALUES}
          loaded={null}
          onSaved={(saved) =>
            history.replace(
              routes.evaluationCaseDetailPath.replace(':id', String(saved.id))
            )
          }
          onReload={() => {}}
          onReloadLatest={() => {}}
        />
      </>
    );
  }

  // Only a first load that failed replaces the page: a failed background reload (after an
  // acknowledgement) keeps the loaded case and any unsaved edits, and says so in a toast.
  if (isError && !data) {
    return (
      <InlineNotification
        kind="error"
        title="Failed to load case"
        hideCloseButton
        lowContrast
      />
    );
  }

  if (isPending || !data) {
    return <InlineLoading description="Loading case..." />;
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle(data.name)}</title>
      </Helmet>
      <CaseForm
        key={savedVersion}
        caseId={caseId}
        initialValues={mapResponseToForm(data)}
        loaded={data}
        onSaved={(saved) => {
          // The eval page reads the case through this same entry, so it sees the save too. Its
          // run check reads the case again: it may now be complete, or have another Type Curve.
          queryClient.setQueryData([CASE_QUERY_KEY, caseId], saved);
          queryClient.invalidateQueries({
            queryKey: [RUN_SCENARIOS_QUERY_KEY],
          });
          setSavedVersion((version) => version + 1);
        }}
        onReload={async () => {
          const reloaded = await refetch();
          if (reloaded.isError) {
            setToast({ message: 'Failed to reload case', type: 'error' });
          }
        }}
        onReloadLatest={async () => {
          const reloaded = await refetch();
          if (reloaded.isError) {
            setToast({ message: 'Failed to reload case', type: 'error' });
            return;
          }
          // The user chose to drop their edits for the latest case: re-mount the form from it.
          setSavedVersion((version) => version + 1);
        }}
      />
    </>
  );
}
